import React, { useState, useContext } from "react";
import "../../profileAdminCard/adminModals.css";
import "./teamModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import { User2, Trash2, Plus, TriangleAlert } from "lucide-react";
import translations from "../../../translations/translations";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import useModalDismiss from "../../common/useModalDismiss";
import { ownerFirst } from "../../../utils/ownerFirst";

const TeamModal = ({ onClose, shopData, onStaffAdded, onStaffRemoved, onRoleUpdated, onInviteSent }) => {
    const { userData, language } = useContext(CombinedContext);
    const team = ownerFirst(shopData, shopData.staffs);
    const [adding, setAdding] = useState(false);
    const [newStaff, setNewStaff] = useState([{ firstName: "", lastName: "", email: "" }]);
    const [loading, setLoading] = useState(false);

    // Local popup state
    const [confirmPopup, setConfirmPopup] = useState({ open: false, member: null });
    const [banner, setBanner] = useState(null);
    const [cancelPopup, setCancelPopup] = useState({ open: false, member: null });
    const [cancelLoading, setCancelLoading] = useState(false);

    /* whichever confirm is on top owns Escape, so it cannot close the modal under it */
    useModalDismiss(onClose, { enabled: !confirmPopup.open && !cancelPopup.open });
    useModalDismiss(() => setConfirmPopup({ open: false, member: null }), { enabled: confirmPopup.open });
    useModalDismiss(() => setCancelPopup({ open: false, member: null }), { enabled: cancelPopup.open && !cancelLoading });

    const isOwner = userData?.user?.role === "owner";
    const canManageRoles = isOwner && shopData?.setup !== true;

    const showBanner = (type, text) => {
        setBanner({ type, text });

        setTimeout(() => {
            setBanner(null);
        }, 3000);
    };

    // The API explains a rejected invite in both languages — prefer that over a generic message.
    const localizedServerMessage = (message) => {
        if (!message) return null;
        if (typeof message === "string") return message;
        return message[language] || message.en || message.es || null;
    };

    const addNewRow = () => setNewStaff((prev) => [...prev, { firstName: "", lastName: "", email: "" }]);
    const removeRow = (index) => setNewStaff((prev) => prev.filter((_, i) => i !== index));

    const updateRow = (index, field, value) => {
        setNewStaff((prev) => {
            const updated = [...prev];
            updated[index][field] = value;
            return updated;
        });
    };

    // ───────────────────────────────────────────────
    // ✅ Add new staff invites
    // ───────────────────────────────────────────────
    const handleConfirmAdd = async () => {
        // 🟢 If not adding, just close
        if (!adding) {
            onClose();
            return;
        }

        if (!userData?.user?.shopId) return;

        setLoading(true);

        try {
            const validStaff = newStaff.filter((s) => s.email.trim());

            if (validStaff.length === 0) {
                showBanner("error", translations[language].teamPage.messages.noValidEmail);
                setLoading(false);
                return;
            }

            const emails = validStaff.map((s) => s.email.trim());
            const firstNames = validStaff.map((s) => s.firstName.trim() || "");
            const lastNames = validStaff.map((s) => s.lastName.trim() || "");

            const res = await companyBasedRequest.invitestaff(
                userData.user.shopId,
                emails,
                firstNames,
                lastNames,
                userData.cognitoTokens.AccessToken
            );

            onStaffAdded?.(res.newStaff);

            // Some addresses were rejected — keep those rows so they can be fixed.
            if (res.failed?.length > 0) {
                const rejected = new Set(res.failed.map((f) => String(f.email || "").trim().toLowerCase()));
                const reason = localizedServerMessage(res.failed[0].error);

                setNewStaff(validStaff.filter((s) => rejected.has(s.email.trim().toLowerCase())));
                showBanner("error", reason || translations[language].teamPage.messages.inviteFailed);

                onInviteSent?.(res.setup);
                return;
            }

            setNewStaff([{ firstName: "", lastName: "", email: "" }]);
            setAdding(false);

            onInviteSent?.(res.setup);
            onClose();
        } catch (err) {
            console.error(err);

            const reason = localizedServerMessage(err.response?.data?.error);
            showBanner("error", reason || translations[language].teamPage.messages.inviteFailed);
        } finally {
            setLoading(false);
        }
    };

    const handlePromote = async (member) => {
        try {
            await companyBasedRequest.promoteStaff(
                userData.user.shopId,
                member.userId,
                userData.user.userId,
                userData.cognitoTokens.AccessToken
            );

            onRoleUpdated?.(member.userId, "admin");
            showBanner(
                "success",
                translations[language].teamPage.toast.promotedToAdmin.replace("{{name}}", member.firstName)
            );
        } catch (err) {
            console.error(err);
            showBanner("error", translations[language].teamPage.messages.promoteFailed);
        }
    };

    const handleDemote = async (member) => {
        try {
            await companyBasedRequest.demoteStaff(
                userData.user.shopId,
                member.userId,
                userData.user.userId,
                userData.cognitoTokens.AccessToken
            );

            onRoleUpdated?.(member.userId, "staff");
            showBanner(
                "success",
                translations[language].teamPage.toast.demotedToStaff.replace("{{name}}", member.firstName)
            );
        } catch (err) {
            console.error(err);
            showBanner("error", translations[language].teamPage.messages.demoteFailed);
        }
    };

    const handleReinvite = async (member) => {
        try {
            await companyBasedRequest.reinviteStaff(shopData.shopId, member.email, userData.cognitoTokens.AccessToken);

            showBanner("success", translations[language].teamPage.toast.inviteResent);
        } catch (err) {
            console.error(err);

            const reason = localizedServerMessage(err.response?.data?.error);
            showBanner("error", reason || translations[language].teamPage.messages.reinviteFailed);
        }
    };

    // Open cancel invite popup
    const handleCancelInvite = (member) => {
        if (member.role === "owner") return;
        setCancelPopup({ open: true, member });
    };

    // Confirm cancel invite
    const confirmCancelInvite = async () => {
        if (!userData?.user?.shopId || !cancelPopup.member) return;
        setCancelLoading(true);

        try {
            await companyBasedRequest.cancelInvite(
                shopData.shopId,
                cancelPopup.member.email,
                userData.cognitoTokens.AccessToken
            );

            // Remove from UI
            onStaffRemoved?.(cancelPopup.member.email);

            showBanner(
                "success",
                translations[language].teamPage.toast.inviteCancelled.replace(
                    "{{name}}",
                    cancelPopup.member.firstName || cancelPopup.member.email
                )
            );
        } catch (err) {
            console.error(err);
            showBanner("error", translations[language].teamPage.messages.cancelInviteFailed);
        } finally {
            setCancelLoading(false);
            setCancelPopup({ open: false, member: null });
        }
    };

    const handleRemoveStaff = (member) => {
        if (member.role === "owner") return;
        setConfirmPopup({ open: true, member });
    };

    const confirmRemove = async () => {
        if (!userData?.user?.shopId || !confirmPopup.member) return;

        try {
            await companyBasedRequest.removeStaff(
                userData.user.shopId,
                confirmPopup.member.userId,
                confirmPopup.member.email,
                userData.cognitoTokens.AccessToken
            );
            onStaffRemoved?.(confirmPopup.member.email);
            showBanner(
                "success",
                translations[language].teamPage.messages.removeSuccess.replace(
                    "{{name}}",
                    confirmPopup.member.firstName || (language === "es" ? "El miembro del equipo" : "The team member")
                )
            );
        } catch (err) {
            console.error(err);
            showBanner("error", translations[language].teamPage.messages.removeFailed);
        } finally {
            setConfirmPopup({ open: false, member: null });
        }
    };

    const t = translations[language].teamPage;

    return (
        <div className="sf-pm-overlay">
            <div className="sf-pm-modal">
                {/* HEADER */}
                <div className="sf-pm-header">
                    <div className="sf-pm-heading">
                        <h2 className="sf-pm-title">{t.header.title}</h2>
                        <p className="sf-pm-sub">{t.modal.team.description}</p>
                    </div>
                    <button className="sf-pm-close" onClick={onClose} aria-label={t.actions.cancel}>
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="sf-pm-body">
                    <section className="sf-pm-section">
                        <div className="sf-pm-label-row">
                            <p className="sf-pm-label">{t.main.roster}</p>
                            {team.length > 0 && <span className="sf-pm-count">{team.length}</span>}
                        </div>

                        {team.length === 0 && !adding && <p className="sf-pm-empty">{t.empty.noStaff}</p>}

                        <div className="sf-pm-team-list">
                            {team.map((m, i) => (
                                <article className="sf-pm-member" key={i}>
                                    <div className="sf-pm-member-head">
                                        {/* AVATAR */}
                                        {m.photo ? (
                                            <img className="sf-pm-member-avatar" src={m.photo} alt="" />
                                        ) : (
                                            <span className="sf-pm-member-avatar is-fallback">
                                                {m.firstName?.charAt(0).toUpperCase()}
                                            </span>
                                        )}

                                        {/* IDENTITY */}
                                        <div className="sf-pm-member-id">
                                            <div className="sf-pm-member-name">
                                                {m.firstName} {m.lastName}
                                            </div>
                                            <div className="sf-pm-member-email">{m.email}</div>
                                        </div>

                                        <span className={`sf-pm-role role-${m.role} status-${m.status}`}>
                                            {t.role[m.role] || t.status[m.status] || m.role}
                                        </span>
                                    </div>

                                    {/* ACTIONS */}
                                    {(canManageRoles || m.status === "invited" || isOwner) && m.role !== "owner" && (
                                        <div className="sf-pm-member-actions">
                                            {canManageRoles && (
                                                <>
                                                    {m.role !== "admin" && m.status === "active" && (
                                                        <button
                                                            className="sf-pm-chip"
                                                            onClick={() => handlePromote(m)}
                                                        >
                                                            {t.actions.promoteToAdmin}
                                                        </button>
                                                    )}

                                                    {m.role === "admin" && (
                                                        <button
                                                            className="sf-pm-chip"
                                                            onClick={() => handleDemote(m)}
                                                        >
                                                            {t.actions.demoteToStaff}
                                                        </button>
                                                    )}
                                                </>
                                            )}

                                            {/* Re-invite (anyone can see) */}
                                            {m.status === "invited" && (
                                                <button className="sf-pm-chip" onClick={() => handleReinvite(m)}>
                                                    {t.actions.reinvite}
                                                </button>
                                            )}

                                            {/* Delete (owner-only) */}
                                            {isOwner && (
                                                <>
                                                    {m.status === "invited" && !m.joined ? (
                                                        <button
                                                            className="sf-pm-chip is-danger"
                                                            onClick={() => handleCancelInvite(m)}
                                                        >
                                                            {t.actions.cancelInvite}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="sf-pm-chip is-danger"
                                                            onClick={() => handleRemoveStaff(m)}
                                                        >
                                                            <Trash2 size={14} strokeWidth={1.9} />
                                                            {t.actions.removeStaff}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </article>
                            ))}

                            {/* Adding new staff */}
                            {adding &&
                                newStaff.map((s, i) => (
                                    <article className="sf-pm-member is-new" key={`new-${i}`}>
                                        <div className="sf-pm-member-head">
                                            <span className="sf-pm-member-avatar is-fallback is-pending">
                                                <User2 size={22} strokeWidth={1.7} />
                                            </span>

                                            <div className="sf-pm-member-id">
                                                <div className="sf-pm-member-name">{t.modal.team.newInvite}</div>
                                                <div className="sf-pm-member-email">{t.modal.team.newInviteHint}</div>
                                            </div>

                                            <button
                                                type="button"
                                                className="sf-pm-iconbtn is-danger"
                                                onClick={() => removeRow(i)}
                                                disabled={newStaff.length === 1}
                                                aria-label={t.actions.removeStaff}
                                            >
                                                <Trash2 size={17} strokeWidth={1.8} />
                                            </button>
                                        </div>

                                        <div className="sf-pm-grid-2">
                                            <div className="sf-pm-field">
                                                <label className="sf-pm-field-label">{t.labels.firstName}</label>
                                                <input
                                                    className="sf-pm-input"
                                                    type="text"
                                                    value={s.firstName}
                                                    onChange={(e) => updateRow(i, "firstName", e.target.value)}
                                                />
                                            </div>
                                            <div className="sf-pm-field">
                                                <label className="sf-pm-field-label">{t.labels.lastName}</label>
                                                <input
                                                    className="sf-pm-input"
                                                    type="text"
                                                    value={s.lastName}
                                                    onChange={(e) => updateRow(i, "lastName", e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="sf-pm-field">
                                            <label className="sf-pm-field-label">{t.labels.emailAddress}</label>
                                            <input
                                                className="sf-pm-input"
                                                type="email"
                                                autoComplete="email"
                                                value={s.email}
                                                onChange={(e) => updateRow(i, "email", e.target.value)}
                                            />
                                        </div>
                                    </article>
                                ))}
                        </div>

                        {/* Add button */}
                        <button
                            type="button"
                            className="sf-pm-add"
                            onClick={() => {
                                if (adding) addNewRow();
                                else setAdding(true);
                            }}
                        >
                            <Plus size={16} strokeWidth={2.2} />
                            {t.actions.addStaff.replace(/^\+\s*/, "")}
                        </button>
                    </section>
                </div>

                {banner && <BannerMessage type={banner.type} text={banner.text} />}

                {/* FOOTER */}
                <div className="sf-pm-footer">
                    <button className="sf-pm-btn is-ghost" onClick={onClose} disabled={loading}>
                        {t.actions.cancel || translations[language].calendar.editor.cancel}
                    </button>

                    <button className="sf-pm-btn is-primary" onClick={handleConfirmAdd} disabled={loading}>
                        {loading && <span className="sf-pm-spinner" aria-hidden="true" />}
                        {loading ? t.actions.sending : translations[language].calendar.editor.save}
                    </button>
                </div>

                {/* Confirm Remove Popup */}
                {confirmPopup.open && (
                    <div className="sf-pm-confirm-overlay">
                        <div className="sf-pm-confirm">
                            <span className="sf-pm-confirm-icon" aria-hidden="true">
                                <TriangleAlert size={22} strokeWidth={2} />
                            </span>
                            <p>
                                <strong>
                                    {t.confirm.popupTitle.replace(
                                        "{{name}}",
                                        confirmPopup.member.firstName || confirmPopup.member.email
                                    )}
                                </strong>
                            </p>
                            <p className="sf-pm-confirm-sub">{t.confirm.warningBookings}</p>
                            <div className="sf-pm-confirm-actions">
                                <button
                                    className="sf-pm-btn is-ghost"
                                    onClick={() => setConfirmPopup({ open: false, member: null })}
                                >
                                    {t.confirm.cancel}
                                </button>
                                <button className="sf-pm-btn is-danger" onClick={confirmRemove}>
                                    {t.confirm.confirm}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Cancel Invite Popup */}
                {cancelPopup.open && (
                    <div className="sf-pm-confirm-overlay">
                        <div className="sf-pm-confirm">
                            <span className="sf-pm-confirm-icon" aria-hidden="true">
                                <TriangleAlert size={22} strokeWidth={2} />
                            </span>
                            <p>
                                <strong>
                                    {t.confirm.cancelInviteTitle.replace(
                                        "{{name}}",
                                        cancelPopup.member.firstName || cancelPopup.member.email
                                    )}
                                </strong>
                            </p>
                            <p className="sf-pm-confirm-sub">{t.confirm.cancelInviteSubtitle}</p>
                            <div className="sf-pm-confirm-actions">
                                <button
                                    className="sf-pm-btn is-ghost"
                                    onClick={() => setCancelPopup({ open: false, member: null })}
                                    disabled={cancelLoading}
                                >
                                    {t.actions.cancel}
                                </button>
                                <button
                                    className="sf-pm-btn is-danger"
                                    onClick={confirmCancelInvite}
                                    disabled={cancelLoading}
                                >
                                    {cancelLoading ? t.actions.removing : t.actions.confirm}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamModal;
